import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import {
  Avatar,
  OffPlatformReviewAvatar,
  ReviewRating,
  UserDisplayName,
  OffPlatformUserDisplayName,
} from '../../components';

import css from './ReviewsAndTestimonialsContainer.css';

// Constants
const INITIAL_DISPLAY_COUNT = 2;

// Custom hook for filtered testimonials
const useFilteredTestimonials = testimonials => {
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);

  useEffect(() => {
    const filtered = testimonials.filter(testimonial => testimonial.state === 'published');
    setFilteredTestimonials(filtered);
  }, [testimonials]);

  return filteredTestimonials;
};

// Custom hook for translation messages
const useTranslationMessages = intl => {
  return useMemo(
    () => ({
      showMore: intl.formatMessage({ id: 'FreelancerAboutMeContainer.showMore' }),
      showLess: intl.formatMessage({ id: 'FreelancerAboutMeContainer.showLess' }),
      testimonialsSubheading: intl.formatMessage({
        id: 'ReviewsAndTestimonialsContainer.testimonialsSubheading',
      }),
    }),
    [intl]
  );
};

// Review component
const Review = ({ review, intl }) => {
  const date = review.createdAt;
  const dateString = intl.formatDate(date, { month: 'long', year: 'numeric' });

  return (
    <div className={css.review}>
      {review.onPlatform ? (
        <Avatar className={css.avatar} user={review.author} disableProfileLink={true} />
      ) : (
        <OffPlatformReviewAvatar className={css.avatar} abbreviatedName={review.abbreviatedName} />
      )}

      <div>
        <ReviewRating
          rating={review.rating}
          className={css.mobileReviewRating}
          reviewStarClassName={css.reviewRatingStar}
        />
        <p className={css.reviewContent}>{review.content}</p>
        <p className={css.reviewInfo}>
          {review.onPlatform ? (
            <UserDisplayName user={review.author} intl={intl} />
          ) : (
            <OffPlatformUserDisplayName authorName={review.authorName} intl={intl} />
          )}
          <span className={css.separator} />
          {dateString}
          <span className={css.desktopSeparator} />
          <span className={css.desktopReviewRatingWrapper}>
            <ReviewRating
              rating={review.rating}
              className={css.desktopReviewRating}
              reviewStarClassName={css.reviewRatingStar}
            />
          </span>
        </p>
      </div>
    </div>
  );
};

// Testimonial component — content is clamped to 5 lines until "Show more..." is clicked
const Testimonial = ({ testimonial, idx, messages }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }
  }, [testimonial.content]);

  const contentClasses = classNames(css.testimonialContent, {
    [css.testimonialContentClamped]: !expanded,
  });

  return (
    <div key={testimonial.id || `testimonial-${idx}`} className={css.testimonial}>
      <div className={css.testimonialAuthorInfo}>
        <div className={css.testimonialTask}>{testimonial.workExperienceTask}</div>
        <div className={css.testimonialCustomerName}>{testimonial.workExperienceCustomerName}</div>
      </div>
      <div>
        <div ref={contentRef} className={contentClasses}>
          "{testimonial.content}"
        </div>
        {isClamped || expanded ? (
          <div className={css.testimonialToggle} onClick={() => setExpanded(prev => !prev)}>
            {expanded ? messages.showLess : messages.showMore}
          </div>
        ) : null}
      </div>
      <div className={css.testimonialAuthorInfo}>
        <div className={css.testimonialAuthorName}>{testimonial.authorName}</div>
        <div className={css.testimonialAuthorPosition}>{testimonial.authorPosition}</div>
      </div>
    </div>
  );
};

// Toggle button component
const ToggleButton = ({ showFull, onToggle, messages }) => (
  <div className={css.toggleShowMore} onClick={onToggle}>
    {showFull ? messages.showLess : messages.showMore}
  </div>
);

// Reviews section component
const ReviewsSection = ({ reviews, showFullReviews, onToggleReviews, messages, intl }) => {
  if (reviews.length === 0) return null;

  const visibleReviews = showFullReviews ? reviews : reviews.slice(0, INITIAL_DISPLAY_COUNT);

  return (
    <div className={css.reviewsContainer}>
      <div className={css.reviewsHeading}>
        <FormattedMessage
          id="ReviewsAndTestimonialsContainer.reviewsHeading"
          values={{ count: reviews.length }}
        />
      </div>
      <div className={css.reviews}>
        {visibleReviews.map(review => (
          <Review key={review.id} review={review} intl={intl} />
        ))}
      </div>
      {reviews.length > INITIAL_DISPLAY_COUNT && (
        <ToggleButton showFull={showFullReviews} onToggle={onToggleReviews} messages={messages} />
      )}
    </div>
  );
};

// Testimonials section component
const TestimonialsSection = ({
  filteredTestimonials,
  showFullTestimonials,
  onToggleTestimonials,
  messages,
}) => {
  if (filteredTestimonials.length === 0) return null;

  const visibleTestimonials = showFullTestimonials
    ? filteredTestimonials
    : filteredTestimonials.slice(0, INITIAL_DISPLAY_COUNT);

  return (
    <div className={css.testimonialsContainer}>
      <div className={css.testimonialHeadingContainer}>
        <div className={css.testimonialsHeading}>
          <FormattedMessage
            id="ReviewsAndTestimonialsContainer.testimonialsHeading"
            values={{ count: filteredTestimonials.length }}
          />
        </div>
        <div className={css.testimonialsSubheading}>{messages.testimonialsSubheading}</div>
      </div>
      <div className={css.testimonials}>
        {visibleTestimonials.map((testimonial, idx) => (
          <Testimonial
            key={testimonial.id || `testimonial-${idx}`}
            testimonial={testimonial}
            idx={idx}
            messages={messages}
          />
        ))}
      </div>
      {filteredTestimonials.length > INITIAL_DISPLAY_COUNT && (
        <ToggleButton
          showFull={showFullTestimonials}
          onToggle={onToggleTestimonials}
          messages={messages}
        />
      )}
    </div>
  );
};

const ReviewsAndTestimonialsContainerComponent = ({ reviews, testimonials, intl }) => {
  const [showFullReviews, setShowFullReviews] = useState(false);
  const [showFullTestimonials, setShowFullTestimonials] = useState(false);

  const filteredTestimonials = useFilteredTestimonials(testimonials);
  const messages = useTranslationMessages(intl);

  // Event handlers
  const toggleReviews = useCallback(() => setShowFullReviews(prev => !prev), []);
  const toggleTestimonials = useCallback(() => setShowFullTestimonials(prev => !prev), []);

  // Early return if no content
  if (reviews.length === 0 && filteredTestimonials.length === 0) {
    return null;
  }

  return (
    <div id="testimonials" className={css.reviewsAndTestimonialsContainer}>
      <ReviewsSection
        reviews={reviews}
        showFullReviews={showFullReviews}
        onToggleReviews={toggleReviews}
        messages={messages}
        intl={intl}
      />
      <TestimonialsSection
        filteredTestimonials={filteredTestimonials}
        showFullTestimonials={showFullTestimonials}
        onToggleTestimonials={toggleTestimonials}
        messages={messages}
      />
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ReviewsAndTestimonialsContainer = compose(
  connect(null, mapDispatchToProps),
  injectIntl
)(ReviewsAndTestimonialsContainerComponent);

export default ReviewsAndTestimonialsContainer;
